// Accordion.jsx
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { NULL_FUNCTION } from '../resource/defaults.js';

const Context = React.createContext(null);

export const Accordion = React.forwardRef(
  (
    {
      as: Component = 'div',
      activeKey = '',
      children = null,
      className = undefined,
    },
    ref
  ) => {
    return (
      <Context.Provider value={activeKey}>
        <Component ref={ref} className={className}>
          {children}
        </Component>
      </Context.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';
Accordion.propTypes = {
  as: PropTypes.elementType,
  activeKey: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export const Collapse = React.forwardRef(
  (
    { as: Component = 'div', className = undefined, children, eventKey },
    ref
  ) => {
    const contextEventKey = useContext(Context);
    const isActive = contextEventKey === eventKey;
    const style = isActive
      ? { overflow: 'hidden' }
      : { maxHeight: 0, overflow: 'hidden', padding: 0 };
    return (
      <Component
        ref={ref}
        className={className}
        style={style}
        selected={isActive}
      >
        {children}
      </Component>
    );
  }
);

Collapse.displayName = 'AccordionCollapse';
Collapse.propTypes = {
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  eventKey: PropTypes.string.isRequired,
};

export const Toggle = React.forwardRef(
  (
    {
      as: Component = 'div',
      eventKey,
      onClick = NULL_FUNCTION,
      children,
      ...restProps
    },
    ref
  ) => {
    const contextEventKey = useContext(Context);
    const isActive = contextEventKey === eventKey;
    return (
      <Component
        ref={ref}
        selected={isActive}
        data-element_id={restProps?.['data-element_id']}
        data-tracking_payload={restProps?.['data-tracking_payload']}
        onClick={event => {
          const eventKeyPassed = eventKey === contextEventKey ? null : eventKey;
          onClick(eventKeyPassed, event);
        }}
      >
        {children}
      </Component>
    );
  }
);

Toggle.displayName = 'AccordionToggle';
Toggle.propTypes = {
  as: PropTypes.elementType,
  eventKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

export default Accordion;
